import { DOCKING_ACTIVATION_RANGE_METERS } from '../../shared/constants.js';
import { getDockingDistanceMeters } from '../../shared/docking.js';
import type { GameSnapshot } from '../../shared/types.js';
import {
  getBearingToMapPoint,
  headingDegreesToCardinal,
  normalizeHeadingDegrees,
  submarineToMapPoint
} from '../../shared/spatial.js';

type DockingUiElements = {
  dockingLightsButton: HTMLButtonElement | null;
  dockedToggleButton: HTMLButtonElement | null;
  dockingDistanceLabel: HTMLElement | null;
  dockingViewportElement: HTMLElement | null;
};

type CreateDockingRoomControllerOptions = {
  resolveActiveMap: () => import('../../shared/maps/types.js').MapDefinition | null;
  getDockingCameraOriginMapPoint: (snapshot: GameSnapshot) => { x: number; y: number; z: number };
};

export const createDockingRoomController = ({
  resolveActiveMap,
  getDockingCameraOriginMapPoint
}: CreateDockingRoomControllerOptions) => {
  const getNearestDockingPortDistanceMeters = (snapshot: GameSnapshot): number => {
    const activeMap = resolveActiveMap();
    if (!activeMap) {
      return Infinity;
    }

    const origin = getDockingCameraOriginMapPoint(snapshot);
    let closestDistance = Infinity;

    const markers = (activeMap as any).glbModelMarkers ?? [];
    for (const marker of markers) {
      if (marker.model.toLowerCase() !== 'docking_port') {
        continue;
      }

      const distance = getDockingDistanceMeters(marker, markers, origin.x, origin.z, origin.y);
      if (distance < closestDistance) {
        closestDistance = distance;
      }
    }

    return closestDistance;
  };

  /** Nearest docking port as a compass bearing from the sub (for guidance when it is not yet in the camera). */
  const getNearestDockingPortBearing = (snapshot: GameSnapshot): { bearing: number; distance: number } | null => {
    const markers = ((resolveActiveMap() as any)?.glbModelMarkers ?? []) as Array<{ model: string; mapX: number; mapZ: number }>;
    const from = submarineToMapPoint(snapshot.submarine);
    let best: { bearing: number; distance: number } | null = null;
    for (const marker of markers) {
      if (marker.model.toLowerCase() !== 'docking_port') continue;
      const distance = Math.hypot(marker.mapX - from.x, marker.mapZ - from.z);
      if (!best || distance < best.distance) {
        best = { bearing: getBearingToMapPoint(from, { x: marker.mapX, z: marker.mapZ }), distance };
      }
    }
    return best;
  };

  /** One-line approach guidance plus a state used for colouring (approach / range / docked / none). */
  const describeApproach = (snapshot: GameSnapshot): { text: string; state: 'approach' | 'range' | 'docked' | 'none' } => {
    if (snapshot.submarine.docked) {
      return { text: 'DOCKED — undock to leave the port', state: 'docked' };
    }
    const distance = getNearestDockingPortDistanceMeters(snapshot);
    if (!Number.isFinite(distance)) {
      return { text: 'No docking port on this map', state: 'none' };
    }
    if (distance <= DOCKING_ACTIVATION_RANGE_METERS) {
      return { text: `IN RANGE · ${distance.toFixed(1)} m — ready to dock`, state: 'range' };
    }
    const port = getNearestDockingPortBearing(snapshot);
    if (!port) {
      return { text: `Docking point ${distance.toFixed(1)} m`, state: 'approach' };
    }
    const error = ((port.bearing - normalizeHeadingDegrees(snapshot.submarine.yaw) + 540) % 360) - 180;
    const turn = Math.abs(error) <= 5 ? 'dead ahead' : `turn ${Math.round(Math.abs(error))}° ${error > 0 ? 'starboard' : 'port'}`;
    return {
      text: `Nearest port ${distance.toFixed(0)} m · BRG ${String(Math.round(port.bearing)).padStart(3, '0')}° ${headingDegreesToCardinal(port.bearing)} · ${turn}`,
      state: 'approach'
    };
  };

  const isDockingInRange = (snapshot: GameSnapshot): boolean => {
    return getNearestDockingPortDistanceMeters(snapshot) <= DOCKING_ACTIVATION_RANGE_METERS;
  };

  const updateUi = (snapshot: GameSnapshot | null, elements: DockingUiElements): void => {
    if (!snapshot) {
      return;
    }

    if (elements.dockingLightsButton) {
      elements.dockingLightsButton.textContent = snapshot.submarine.dockingLightsOn ? 'Turn docking lights OFF' : 'Turn docking lights ON';
    }

    if (elements.dockedToggleButton) {
      const isDocked = snapshot.submarine.docked;
      const dockingInRange = isDockingInRange(snapshot);
      const canDock = isDocked || dockingInRange;
      elements.dockedToggleButton.textContent = isDocked ? 'Undock' : 'Dock';
      elements.dockedToggleButton.disabled = !canDock;
      elements.dockedToggleButton.title = isDocked
        ? 'Undock from the current docking port.'
        : `Move within ${DOCKING_ACTIVATION_RANGE_METERS}m of a docking port to dock.`;
    }

    if (elements.dockingDistanceLabel) {
      const approach = describeApproach(snapshot);
      elements.dockingDistanceLabel.style.display = 'block';
      elements.dockingDistanceLabel.textContent = approach.text;
      elements.dockingDistanceLabel.dataset.state = approach.state;
    }

    if (elements.dockingViewportElement) {
      elements.dockingViewportElement.classList.toggle('docking-range-ok', isDockingInRange(snapshot));
    }
  };

  return {
    describeApproach,
    getNearestDockingPortDistanceMeters,
    isDockingInRange,
    updateUi
  };
};
